/**
 * Labstep
 *
 * @module screens/ExperimentWorkflow/Index/Master
 * @desc ExperimentWorkflow Index Master
 */

import type { EntitySearchContainerChildrenProps } from 'labstep-web/components/Entity/Search/Children/types';
import { ExperimentWorkflowActionCreateMenu } from 'labstep-web/components/ExperimentWorkflow/Action/CreateMenu';
import { ExperimentWorkflowEmptyState } from 'labstep-web/components/ExperimentWorkflow/EmptyState';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridNewIndex } from 'labstep-web/grid/NewIndex';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { ExperimentWorkflow } from 'labstep-web/models/experiment-workflow.model';
import { FolderType } from 'labstep-web/models/folder.model';
import { ExperimentWorkflowIndexBreadcrumb } from 'labstep-web/screens/ExperimentWorkflow/Index/Breadcrumb';
import { ExperimentWorkflowIndexGridLeftPane } from 'labstep-web/screens/ExperimentWorkflow/Index/Grid/LeftPane';
import { MasterIndex } from 'labstep-web/screens/Master/Index';
import { LocationService } from 'labstep-web/services/location.service';
import { selectFolderIncludeSubfolders } from 'labstep-web/state/new/user-settings';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bulkActions, filters } from './constants';
import type { ScreensExperimentWorkflowIndexGridProps } from './types';

export const ScreensExperimentWorkflowIndexGrid: React.FC<
  ScreensExperimentWorkflowIndexGridProps
> = ({ entityView, group, location }) => {
  const folderId = LocationService.getQueryParameter(
    location,
    'folder_id',
  );
  const isUnsorted = LocationService.getQueryParameter(
    location,
    'is_unsorted',
  );
  const hasSignatureRequest = LocationService.getQueryParameter(
    location,
    'has_signature_request',
  );
  const hasSignature = LocationService.getQueryParameter(
    location,
    'has_signature',
  );
  const signatureRequestUserId = LocationService.getQueryParameter(
    location,
    'signature_request_user_id',
  );
  const entityStateName = LocationService.getQueryParameter(
    location,
    'entity_state_name',
  );
  const isStarted = LocationService.getQueryParameter(
    location,
    'is_started',
  );
  const isEnded = LocationService.getQueryParameter(
    location,
    'is_ended',
  );
  const includeSubfolders = useSelector(
    selectFolderIncludeSubfolders(FolderType.experiment_workflow),
  );

  const action = useCallback(({ searchParams }: any) => {
    const folderId = searchParams.folder_id;
    return (
      <ExperimentWorkflowActionCreateMenu
        body={folderId ? { folder_id: folderId } : undefined}
      />
    );
  }, []);
  const noResultsMessage = useCallback(
    ({ searchParams }: any) => (
      <ExperimentWorkflowEmptyState
        action={action({ searchParams })}
      />
    ),
    [action],
  );

  const globalParams: Record<
    string,
    string | number | Record<string, unknown>[] | boolean | undefined
  > = {
    group_id: group.id,
    folder_id: folderId,
    is_unsorted: isUnsorted === 'true' ? true : undefined,
    include_subfolders: includeSubfolders,
    has_signature_request:
      hasSignatureRequest === 'true' ? true : undefined,
    has_signature: hasSignature === 'true' ? true : undefined,
    entity_state_name: entityStateName || undefined,
    is_started: isStarted === 'true' ? true : undefined,
    is_ended: isEnded === 'true' ? true : undefined,
    signature_request_user_id: signatureRequestUserId,
    skip_total: 1,
  };

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'experiment_workflow_index',
    globalParams,
    entityView,
    loadedEntityView: new EntityView({
      ...entityView,
    }),
    initialSearchParams: entityView.parameters || {},
  };
  if (paramsStateContext.loadedEntityView) {
    paramsStateContext.loadedEntityView.parameters =
      paramsStateContext.initialSearchParams;
  }

  // Check if there's a 'folders' column in the loaded entity view
  const hasFoldersColumn =
    paramsStateContext.entityView?.column_definition_ids?.some(
      (colId) => colId === 'folders',
    );

  // Check if there's a 'tags' column in the loaded entity view
  const hasTagsColumn =
    paramsStateContext.entityView?.column_definition_ids?.some(
      (colId) => colId === 'tags',
    );

  // Check if there's a 'linked_experiments' column in the loaded entity view
  const hasLinkedExperimentsColumn =
    paramsStateContext.entityView?.column_definition_ids?.some(
      (colId) => colId === 'linked_experiments',
    );

  // Build serializerGroups conditionally
  const baseSerializerGroups = 'default,experiment_workflow_index';
  let serializerGroups = baseSerializerGroups;
  if (hasFoldersColumn) {
    serializerGroups += ',experiment_workflow_folders';
  }
  if (hasTagsColumn) {
    serializerGroups += ',experiment_workflow_tags';
  }
  if (hasLinkedExperimentsColumn) {
    serializerGroups += ',experiment_workflow_linked_experiments';
  }

  const params = {
    ...paramsStateContext.initialSearchParams,
    serializerGroups,
  };

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <ExportProgressProvider>
        <MasterIndex
          entityName={ExperimentWorkflow.entityName}
          breadcrumb={<ExperimentWorkflowIndexBreadcrumb />}
          leftPane={<ExperimentWorkflowIndexGridLeftPane />}
          filters={filters}
          params={params}
          noResultsMessage={noResultsMessage}
          action={action}
          bulkActions={bulkActions}
          useAllPages
          usePostFilter
          tableFormat={false}
          showExperimentWorkflowViewManager
        >
          {({
            entities,
            readNextPage,
            status,
          }: EntitySearchContainerChildrenProps) => (
            <GridNewIndex
              entityName={ExperimentWorkflow.entityName}
              group={group}
              entities={entities}
              entityView={entityView}
              status={status}
              loadMore={readNextPage}
            />
          )}
        </MasterIndex>
      </ExportProgressProvider>
    </ParamsStateContextProvider>
  );
};

export default withRouter(ScreensExperimentWorkflowIndexGrid);
